/**
 * Fills real per-run textAdvance/textWidth/baseline (and the line's own textWidth/descent)
 * before a TextLine is sent to the API. These are never computed by the content-editable edit
 * path — local rendering never reads them (the browser shapes/flows glyphs itself), but yb360's
 * recomposed-XML renderer does: runs saved with these left at 0 render with glyphs
 * collapsed/overlapping and the text sitting on the bounding box (baseline 0 = line top).
 */

import type { TextLine, TextRun } from '@/api/types/stowed-element'
import { DEFAULT_FONT_FAMILY_ID, DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'

import {
  CANVAS_FONT_MIN_PT,
  letterSpacingCss,
  measureFontAscentDescentPt,
  resolveCanvasFont,
} from '@/lib/text/canvas-font-metrics'

let sharedCanvas: HTMLCanvasElement | null = null

function getMeasurementCtx(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!sharedCanvas) sharedCanvas = document.createElement('canvas')

  return sharedCanvas.getContext('2d')
}

function measureRunWidthPt(run: TextRun, fonts?: ParagraphBlockFontLookup): number {
  const text = run.textContent ?? ''

  if (text.length === 0) return 0

  const pointSize = run.pointSize ?? DEFAULT_POINT_SIZE
  const ctx = getMeasurementCtx()

  if (!ctx) return text.length * pointSize * 0.5

  const sizeUsed = Math.max(CANVAS_FONT_MIN_PT, pointSize)

  ctx.font = resolveCanvasFont(
    pointSize,
    run.fontFamilyId ?? DEFAULT_FONT_FAMILY_ID,
    run.fontId,
    fonts
  )
  // Both of these must be assigned AFTER `font`: the canvas `font` setter parses the CSS `font`
  // shorthand, which resets `font-kerning` (and letter-spacing) to its initial value. Same reason
  // every letterSpacing assignment in this codebase follows its `font` assignment.
  //
  // Unkerned on purpose: yb360 sums per-character advances and discards all pair kerning
  // (odeditorsvg.js:13318-13322), and both larry paint paths force `fontKerning: 'none'` for the
  // same reason (WMPCM-8161 — kerned ink drifts left of an unkerned advance sum, cumulatively
  // across a row). Measuring kerned would bake `TA`/`TW` narrower than either renderer paints.
  ctx.fontKerning = 'none'
  ctx.letterSpacing = letterSpacingCss(run.tracking ?? 0, run.kerning ?? 0, sizeUsed)

  // Scale back down when the floor applied, exactly as `measureFontAscentDescentPt` does: a 6pt run
  // is measured at CANVAS_FONT_MIN_PT but paints at 6, so the raw width would be ~25% too wide.
  // Otherwise this is already points — the font was set at `${pointSize}px` and 1pt = 1 CSS px.
  return (ctx.measureText(text).width / sizeUsed) * pointSize
}

/** Fills textAdvance/textWidth on every run of `line` with measured values, maintaining a
 *  cumulative cursor starting at the line's leftMargin, plus the vertical wire metrics yb360
 *  needs: per-run baseline (BL, identical on every run of a line) and the line's descent (DES).
 *  Returns the line with its own textWidth set to the sum of its run widths. Run granularity is
 *  taken as given — this function never splits or merges runs. Shape text is pre-coalesced by its
 *  caller (`buildWireParagraphs(..., coalesceRuns=true)` in stowed-element-builder.ts) so adjacent
 *  same-style glyphs arrive as one run and get a single summed width here; text frames pass their
 *  runs through unchanged.
 *
 *  `absoluteBaselinePt` overrides the derived baseline. yb360's `BL` is measured from the top of
 *  the frame, not from the line's own top: every one of a sprite's tspans goes into a single
 *  `svgContentGroup` with no per-line transform, painted at `y = sprite.padding + bl`
 *  (odxml.js:1444), and yb360's own wrapped shape text carries 13 / 27.4 / 41.8 across three lines.
 *  Callers that emit more than one visual line per frame (shape text — see
 *  `bake-shape-text-wire-lines.ts`) must pass the row's frame-absolute baseline, or every line
 *  lands on top of the first. Omitted, the derived per-line ascent is used, which is correct for
 *  the one-line-per-paragraph text-frame path.
 *
 *  `segmentBreaks` handles a row an obstacle split into more than one horizontal chunk (text
 *  resumes on the far side of a shape, on the same visual row): one entry per chunk after the
 *  first, giving the index into `line.textRuns` where that chunk's runs begin and the absolute x
 *  the cursor must jump to there. Without this the cursor below just keeps summing continuously
 *  from the previous chunk's end, silently closing the gap the shape-text engine left around the
 *  obstacle — `bake-shape-text-wire-lines.ts` is the only caller that ever has more than one
 *  chunk on a row, so every other caller omits this and gets the previous single-cursor
 *  behavior unchanged. */
export function fillTextLineWireMetrics(
  line: TextLine,
  fonts?: ParagraphBlockFontLookup,
  absoluteBaselinePt?: number,
  segmentBreaks?: Array<{ runIndex: number; x: number }>
): TextLine {
  const startAdvance = line.leftMargin ?? 0
  const ctx = getMeasurementCtx()
  let cursor = startAdvance
  let maxAscent = 0
  let maxDescent = 0
  let nextBreak = 0
  const measuredRuns = (line.textRuns ?? []).map((run, index) => {
    if (
      segmentBreaks &&
      nextBreak < segmentBreaks.length &&
      segmentBreaks[nextBreak].runIndex === index
    ) {
      cursor = segmentBreaks[nextBreak].x
      nextBreak++
    }

    const width = measureRunWidthPt(run, fonts)
    const advance = cursor

    cursor += width

    // Empty runs still occupy a line in yb360, so they contribute vertical metrics too.
    const vertical = measureFontAscentDescentPt(
      ctx,
      run.pointSize ?? DEFAULT_POINT_SIZE,
      run.fontFamilyId ?? DEFAULT_FONT_FAMILY_ID,
      run.fontId,
      fonts
    )

    maxAscent = Math.max(maxAscent, vertical.ascentPt)
    maxDescent = Math.max(maxDescent, vertical.descentPt)

    return { ...run, textAdvance: advance, textWidth: width }
  })
  const baseline =
    absoluteBaselinePt != null && Number.isFinite(absoluteBaselinePt)
      ? Math.round(absoluteBaselinePt * 100) / 100
      : Math.round(maxAscent * 100) / 100
  const textRuns = measuredRuns.map((run) => ({ ...run, baseline }))

  return {
    ...line,
    textRuns,
    textWidth: cursor - startAdvance,
    // Integer on purpose: the protobuf snapshot stores TextLine.descent as uint32.
    descent: Math.round(maxDescent),
  }
}
